import { DEFAULT_EXPERIMENT_VALUES } from 'constants/experimentDefaults'
import { Box, CircularProgress, Theme, Typography } from '@mui/material'
import React from 'react'

import { RMEResponse } from 'utils/api'
import { useAuth } from 'services/auth'
import EstimatesTable from './EstimatesTable'
import { ExperimentData } from './types'

interface PerformanceEstimateProps {
  experimentData: ExperimentData
  metricsResult: RMEResponse | null
  isLoadingMetrics: boolean
  theme: Theme
}

const PerformanceEstimate: React.FC<PerformanceEstimateProps> = ({
  experimentData,
  metricsResult,
  isLoadingMetrics,
  theme,
}) => {
  const { isInternalUser } = useAuth()

  const formatDuration = (totalMinutes: number): string => {
    const hours = Math.floor(totalMinutes / 60)
    const minutes = totalMinutes % 60

    if (hours === 0) {
      return `${minutes}m`
    }
    if (minutes === 0) {
      return `${hours}h`
    }
    return `${hours}h ${minutes}m`
  }

  const getEstimatedDuration = () => {
    if (metricsResult) {
      const minutes = Math.round(metricsResult.elapsed_time_seconds / 60)
      return formatDuration(minutes)
    }
    return formatDuration(DEFAULT_EXPERIMENT_VALUES.ESTIMATED_RUN_DURATION)
  }

  return (
    <Box>
      <Box
        sx={{
          backgroundColor: theme.palette.info.light,
          p: 2,
          borderRadius: 1,
          mb: 2,
          borderBottom: `1px solid ${'#d0d0d0'}`,
          height: '73px',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 400,
            color: theme.palette.text.primary,
            fontSize: '18px',
            lineHeight: '20px',
          }}
        >
          Performance Estimate
        </Typography>
      </Box>

      <Box sx={{ px: 1 }}>
        {isLoadingMetrics && (
          <Box
            sx={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 9999,
            }}
          >
            <CircularProgress size={40} />
          </Box>
        )}

        <Box
          sx={{
            backgroundColor: theme.palette.background.default,
            border: `1px solid ${'#d0d0d0'}`,
            borderRadius: 1,
            overflow: 'hidden',
            mb: 2,
          }}
        >
          {/* Run Duration */}
          <Box sx={{ display: 'flex', height: '48px', borderBottom: `1px solid ${'#d0d0d0'}` }}>
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                px: 2,
                borderRight: `1px solid ${'#d0d0d0'}`,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontSize: '14px',
                  color: theme.palette.text.primary,
                  fontWeight: '40',
                  lineHeight: '18px',
                }}
              >
                Estimated Run Duration
              </Typography>
            </Box>
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                paddingLeft: '10px',
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  fontSize: '14px',
                  color: theme.palette.text.primary,
                  fontWeight: '40',
                  lineHeight: '18px',
                }}
              >
                {getEstimatedDuration()}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Show estimates table for internal users and external customers based on mode */}
        {(experimentData.experimentGoal === 'purity' ||
          experimentData.experimentGoal === 'yield') && (
          <Box sx={{ mt: 2, mb: 2 }}>
            <EstimatesTable
              metricsResult={metricsResult}
              wells={experimentData.wells}
              experimentGoal={experimentData.experimentGoal}
              isInternalUser={isInternalUser}
            />
          </Box>
        )}

        {metricsResult && metricsResult.elapsed_time_seconds > 36000 && (
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="body2"
              sx={{
                fontSize: '14px',
                color: theme.palette.warning.main,
                lineHeight: '20px',
                fontWeight: 400,
                mb: 2,
              }}
            >
              Warning: Run duration is greater than the maximum run duration on REM-I of 10 hours.
            </Typography>
          </Box>
        )}

        {/* Display API warnings if they exist */}
        {metricsResult && metricsResult.warnings && metricsResult.warnings.length > 0 && (
          <Box sx={{ mb: 2 }}>
            {metricsResult.warnings.map((warning, index) => {
              // Create a unique key based on warning content to avoid array index key ESLint error
              const warningKey = `warning-${warning
                .replace(/\s+/g, '-')
                .toLowerCase()
                .substring(0, 30)}-${warning.length}`
              const isLastWarning = index === (metricsResult.warnings?.length ?? 0) - 1

              return (
                <Typography
                  key={warningKey}
                  variant="body2"
                  sx={{
                    fontSize: '14px',
                    color: theme.palette.warning.main,
                    lineHeight: '20px',
                    fontWeight: 400,
                    mb: isLastWarning ? 2 : 1,
                  }}
                >
                  Warning: {warning}
                </Typography>
              )
            })}
          </Box>
        )}

        {/* General disclaimer for all experiment types */}
        <Typography
          variant="body2"
          sx={{
            fontSize: '14px',
            color: theme.palette.text.secondary,
            lineHeight: '20px',
            fontWeight: 400,
            mb: 2,
          }}
        >
          Performance metrics will be calculated automatically as you adjust experiment parameters.
        </Typography>

        {/* Additional note about estimates for sorting experiments */}
        {(experimentData.experimentGoal === 'purity' ||
          experimentData.experimentGoal === 'yield') && (
          <Typography
            variant="body2"
            sx={{
              fontSize: '14px',
              color: theme.palette.text.primary,
              lineHeight: '20px',
              fontWeight: 400,
              mb: 2,
            }}
          >
            This is a rough estimate. Actual performance will vary with sample composition.
          </Typography>
        )}

        <Typography
          variant="body2"
          sx={{
            fontSize: '14px',
            color: theme.palette.text.secondary,
            lineHeight: '20px',
            fontWeight: 400,
            fontStyle: 'italic',
            mb: 6,
          }}
        >
          Estimates are calibrated using a control cell line mixture. Actual performance may vary with different cell types and sizes.
        </Typography>
      </Box>
    </Box>
  )
}

export default PerformanceEstimate


